import {
  Award,
  Briefcase,
  Check,
  Download,
  Mail,
  Pencil,
  Phone,
  Plus,
  Trash2,
  Upload,
  UserCheck,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import API from "../../lib/api";

// Fixed catalog the super admin picks from. Keep in sync with the public
// site's service list so badges line up across customer-facing pages.
const SPECIALIZATION_OPTIONS = [
  "ITR Filing",
  "GST Filing",
  "Audit",
  "Company Incorporation",
  "LLP Incorporation",
  "Trademarks & Patents",
  "Business Loans (MSME/Mudra/CGTMSE)",
  "Business Insurance",
  "CFO Services",
  "Labour Law Compliance",
  "CA Consulting",
  "Virtual Accountant",
  "TDS / TCS Compliance",
  "ROC Compliance",
  "Bookkeeping",
  "Tax Planning",
  "International Taxation",
  "Transfer Pricing",
];

// Photo paths come back as `/uploads/ca-photos/<file>` (relative to the API
// host). Prefix with REACT_APP_BACKEND_URL so they render from the admin panel.
const resolvePhotoUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/") && process.env.REACT_APP_BACKEND_URL) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

const RequiredStar = () => <span className="text-red-500"> *</span>;

const EMPTY_FORM = {
  name: "",
  registration_no: "",
  qualification: "Consultant Chartered Accountant",
  email: "",
  phone: "",
  experience_yrs: "",
  specializations: "",
  credly_url: "",
  bio: "",
  photo_url: "",
  is_active: true,
  password: "",
};

const isValidCredlyUrl = (raw) => {
  const url = String(raw || "").trim();
  if (!url) return false;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:")
      return false;
    return /(^|\.)calendly\.com$/i.test(parsed.hostname);
  } catch {
    return false;
  }
};

function Avatar({ name, photo_url, size = 48 }) {
  const src = resolvePhotoUrl(photo_url);
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size }}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    );
  }
  const initials = name
    ? name
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "?";
  return (
    <div
      className="rounded-full bg-[#6C63FF] flex items-center justify-center text-white font-heading font-bold flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials}
    </div>
  );
}

function ProfileCard({ profile, onEdit, onDelete, onToggle }) {
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete CA profile for ${profile.name}? This cannot be undone.`,
      )
    )
      return;
    setDeleting(true);
    await onDelete(profile.id);
    setDeleting(false);
  };

  const handleToggle = async () => {
    setToggling(true);
    await onToggle(profile.id, !profile.is_active);
    setToggling(false);
  };

  const specs = profile.specializations
    ? profile.specializations
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  return (
    <div
      className={`bg-white rounded-2xl border-2 shadow-sm transition-all ${
        profile.is_active
          ? "border-[#E8E6F8]"
          : "border-dashed border-gray-200 opacity-60"
      }`}
    >
      {/* Header */}
      <div className="p-5 flex items-start gap-4">
        <Avatar name={profile.name} photo_url={profile.photo_url} size={56} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-heading font-bold text-[#1A1A2E]">
              {profile.name}
            </h3>
            <span
              className={`text-[10px] font-body px-2 py-0.5 rounded-full ${
                profile.is_active
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {profile.is_active ? "Active" : "Inactive"}
            </span>
          </div>
          <p className="text-xs text-[#6C63FF] font-body font-medium mt-0.5">
            {profile.qualification}
          </p>
          <p className="text-xs text-[#9CA3AF] font-mono mt-0.5">
            ICAI: {profile.registration_no}
          </p>
        </div>
      </div>

      {/* Details grid */}
      <div className="px-5 pb-4 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-body">
          {profile.email && (
            <div className="flex items-center gap-1.5 text-[#4A4A60]">
              <Mail className="h-3 w-3 text-[#6C63FF] flex-shrink-0" />
              <span className="truncate">{profile.email}</span>
            </div>
          )}
          {profile.phone && (
            <div className="flex items-center gap-1.5 text-[#4A4A60]">
              <Phone className="h-3 w-3 text-[#6C63FF] flex-shrink-0" />
              <span>{profile.phone}</span>
            </div>
          )}
          {profile.experience_yrs > 0 && (
            <div className="flex items-center gap-1.5 text-[#4A4A60]">
              <Briefcase className="h-3 w-3 text-[#6C63FF] flex-shrink-0" />
              <span>{profile.experience_yrs} yrs experience</span>
            </div>
          )}
        </div>

        {specs.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {specs.map((s) => (
              <span
                key={s}
                className="text-[10px] font-body px-2 py-0.5 bg-[#F5F4FB] text-[#6C63FF] rounded-full border border-[#E8E6F8]"
              >
                {s}
              </span>
            ))}
          </div>
        )}

        {profile.bio && (
          <p className="text-xs text-[#6B7280] font-body leading-relaxed line-clamp-2 mt-2 italic">
            "{profile.bio}"
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 py-3 border-t border-[#E8E6F8] flex items-center gap-2">
        <button
          onClick={() => onEdit(profile)}
          className="flex items-center gap-1 text-xs font-body text-[#6C63FF] hover:underline"
        >
          <Pencil className="h-3 w-3" /> Edit
        </button>
        <span className="text-[#E8E6F8]">·</span>
        <button
          onClick={handleToggle}
          disabled={toggling}
          className={`text-xs font-body ${profile.is_active ? "text-orange-500 hover:underline" : "text-green-600 hover:underline"}`}
        >
          {toggling ? "…" : profile.is_active ? "Deactivate" : "Activate"}
        </button>
        <span className="text-[#E8E6F8]">·</span>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="flex items-center gap-1 text-xs font-body text-red-500 hover:underline ml-auto"
        >
          <Trash2 className="h-3 w-3" />
          {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  );
}

function ProfileForm({ initial, onSave, onCancel }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial ? { ...EMPTY_FORM, ...initial, password: "" } : EMPTY_FORM,
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // specializations is stored as a comma-separated string on the wire and
  // in the DB; we project it to/from an array for the multi-select UI.
  const selectedSpecs = (form.specializations || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const toggleSpec = (spec) => {
    const next = new Set(selectedSpecs);
    if (next.has(spec)) next.delete(spec);
    else next.add(spec);
    set("specializations", Array.from(next).join(", "));
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Photo must be 5 MB or smaller");
      e.target.value = "";
      return;
    }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const { data } = await API.post("/admin/ca-profiles/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_url", data.photo_url);
    } catch (err) {
      setError(err.response?.data?.detail || "Photo upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Existing CA records may have `null` for fields added later (e.g.
    // credly_url), so coerce to string before .trim() to avoid a crash.
    const s = (v) => (v == null ? "" : String(v)).trim();
    if (!s(form.name)) {
      setError("Full Name is required");
      return;
    }
    if (!s(form.registration_no)) {
      setError("ICAI Membership No. is required");
      return;
    }
    if (!s(form.qualification)) {
      setError("Qualification is required");
      return;
    }
    if (!s(form.email)) {
      setError("Email is required");
      return;
    }
    if (!s(form.phone)) {
      setError("Phone is required");
      return;
    }
    if (
      form.experience_yrs === "" ||
      form.experience_yrs === null ||
      isNaN(Number(form.experience_yrs))
    ) {
      setError("Experience (years) is required");
      return;
    }
    if (selectedSpecs.length === 0) {
      setError("Pick at least one specialization");
      return;
    }
    if (!s(form.credly_url)) {
      setError("Calendly URL is required");
      return;
    }
    if (!isValidCredlyUrl(form.credly_url)) {
      setError("Calendly URL must be a valid https://calendly.com/… link");
      return;
    }
    if (!isEdit && (!form.password || form.password.length < 8)) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (isEdit && form.password && form.password.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const fields = [
    {
      key: "name",
      label: "Full Name",
      type: "text",
      placeholder: "CA Ravi Sharma",
      required: true,
    },
    {
      key: "registration_no",
      label: "ICAI Membership No.",
      type: "text",
      placeholder: "e.g. 123456",
      required: true,
    },
    {
      key: "qualification",
      label: "Qualification",
      type: "text",
      placeholder: "Chartered Accountant",
      required: true,
    },
    {
      key: "email",
      label: "Email",
      type: "email",
      placeholder: "ca@example.com",
      required: true,
    },
    {
      key: "phone",
      label: "Phone",
      type: "tel",
      placeholder: "+91 98765 43210",
      required: true,
    },
    {
      key: "experience_yrs",
      label: "Experience (years)",
      type: "number",
      placeholder: "10",
      required: true,
    },
    {
      key: "credly_url",
      label: "Calendly URL",
      type: "url",
      placeholder: "https://calendly.com/your-handle",
      required: true,
    },
  ];

  const previewSrc = resolvePhotoUrl(form.photo_url);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-[#F5F4FB] border border-[#E8E6F8] rounded-lg px-3 py-2 text-xs text-[#6B7280] font-body">
        Creating a CA profile here also creates the matching{" "}
        <strong className="text-[#1A1A2E]">CA login</strong> in the Team page.
        The email and password below are what the CA will use to sign in.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map(({ key, label, type, placeholder, required }) => (
          <div key={key}>
            <label className="text-xs font-body text-[#6B7280] mb-1 block">
              {label}
              {required && <RequiredStar />}
            </label>
            <input
              type={type}
              value={form[key]}
              onChange={(e) => set(key, e.target.value)}
              placeholder={placeholder}
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            />
          </div>
        ))}
        <div>
          <label className="text-xs font-body text-[#6B7280] mb-1 block">
            {isEdit ? (
              "New Password (leave blank to keep current)"
            ) : (
              <>
                Password
                <RequiredStar />
              </>
            )}
          </label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            placeholder={isEdit ? "••••••••" : "At least 8 characters"}
            autoComplete="new-password"
            className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
          />
        </div>
      </div>

      {/* Specializations — multi-select chips */}
      <div>
        <label className="text-xs font-body text-[#6B7280] mb-2 block">
          Specializations
          <RequiredStar />
          <span className="ml-2 text-[10px] text-[#9CA3AF]">
            Click to toggle — pick at least one
          </span>
        </label>
        <div className="flex flex-wrap gap-2 p-3 border border-[#E8E6F8] rounded-lg bg-[#F5F4FB]/40">
          {SPECIALIZATION_OPTIONS.map((opt) => {
            const active = selectedSpecs.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggleSpec(opt)}
                className={`text-xs font-body px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? "bg-[#6C63FF] text-white border-[#6C63FF]"
                    : "bg-white text-[#4A4A60] border-[#E8E6F8] hover:border-[#6C63FF]/40"
                }`}
              >
                {active ? "✓ " : ""}
                {opt}
              </button>
            );
          })}
        </div>
        {selectedSpecs.length > 0 && (
          <p className="text-[10px] text-[#9CA3AF] font-body mt-1.5">
            {selectedSpecs.length} selected
          </p>
        )}
      </div>

      {/* Profile photo uploader */}
      <div>
        <label className="text-xs font-body text-[#6B7280] mb-2 block">
          Profile Photo
          <span className="ml-2 text-[10px] text-[#9CA3AF]">
            JPG / PNG / WebP, max 5 MB — optional
          </span>
        </label>
        <div className="flex items-center gap-4">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="Profile preview"
              className="w-20 h-20 rounded-xl object-cover border border-[#E8E6F8]"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className="w-20 h-20 rounded-xl border-2 border-dashed border-[#E8E6F8] bg-[#F5F4FB] flex items-center justify-center text-[#9CA3AF]">
              <UserCheck className="h-7 w-7" />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handlePhotoSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 text-xs font-body font-medium px-3 py-2 rounded-lg bg-[#6C63FF]/10 text-[#6C63FF] hover:bg-[#6C63FF]/20 transition-colors disabled:opacity-60"
            >
              <Upload className="h-3.5 w-3.5" />
              {uploading
                ? "Uploading…"
                : previewSrc
                  ? "Replace photo"
                  : "Choose photo"}
            </button>
            {form.photo_url && (
              <button
                type="button"
                onClick={() => set("photo_url", "")}
                className="text-[10px] text-red-500 hover:underline font-body text-left"
              >
                Remove photo
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs font-body text-[#6B7280] mb-1 block">
          Bio / Description
        </label>
        <textarea
          rows={3}
          value={form.bio}
          onChange={(e) => set("bio", e.target.value)}
          placeholder="Brief description about this CA's expertise and approach…"
          className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        />
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
            className="accent-[#6C63FF]"
          />
          <span className="text-sm font-body text-[#1A1A2E]">
            Active (visible to clients)
          </span>
        </label>
      </div>

      {error && (
        <p className="text-xs text-red-500 font-body bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#6C63FF] hover:bg-[#5B52CC] text-white text-sm font-body font-semibold rounded-xl transition-colors disabled:opacity-60"
        >
          <Check className="h-4 w-4" />
          {saving ? "Saving…" : initial ? "Save Changes" : "Add CA Profile"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-2 px-5 py-2.5 border border-[#E8E6F8] text-[#6B7280] text-sm font-body rounded-xl hover:bg-[#F5F4FB] transition-colors"
        >
          <X className="h-4 w-4" /> Cancel
        </button>
      </div>
    </form>
  );
}

export default function CAProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(null); // null | 'add' | profile object (edit)

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await API.get("/admin/ca-profiles");
      setProfiles(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load CA profiles");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (form) => {
    if (mode && mode !== "add") {
      await API.put(`/admin/ca-profiles/${mode.id}`, form);
    } else {
      await API.post("/admin/ca-profiles", form);
    }
    setMode(null);
    await load();
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/admin/ca-profiles/${id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleToggle = async (id, is_active) => {
    try {
      await API.put(`/admin/ca-profiles/${id}`, { is_active });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Update failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isMigrationError = error && error.toLowerCase().includes("migration");

  const handleExport = () => {
    // Profiles are already fully loaded in state (no pagination on this page),
    // so we can build the CSV directly without re-fetching.
    const headers = [
      "Profile ID", "Name", "ICAI Membership No.", "Qualification",
      "Email", "Phone", "Experience (yrs)", "Specializations",
      "Calendly URL", "Status", "Bio", "Created",
    ];

    // RFC 4180: quote when the value contains comma / quote / newline.
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    // Specializations come back as either an array or a comma-separated
    // string depending on schema — normalise to `a; b; c` so CSV stays clean.
    const flatten = (raw) => {
      if (Array.isArray(raw)) return raw.join("; ");
      if (!raw) return "";
      return String(raw).split(",").map((s) => s.trim()).filter(Boolean).join("; ");
    };

    const rows = profiles.map((p) => [
      `#${p.id}`,
      p.name,
      p.registration_no,
      p.qualification,
      p.email,
      p.phone,
      p.experience_yrs,
      flatten(p.specializations),
      p.credly_url,
      p.is_active ? "Active" : "Inactive",
      p.bio,
      p.created_at ? new Date(p.created_at).toLocaleString("en-IN") : "",
    ].map(esc).join(","));

    const csv = [headers.map(esc).join(","), ...rows].join("\r\n");
    // UTF-8 BOM so Excel renders Indian names correctly.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `ca-profiles-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#7C2D12] via-[#C2410C] to-[#F59E0B] text-white px-6 py-6">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-[#FBBF24]/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#F97316]/30 rounded-full blur-3xl" />
        <div className="relative flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <UserCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                CA Team Profiles
              </h1>
              <p className="text-sm text-white/85 font-body mt-0.5">
                {profiles.length} profile{profiles.length !== 1 ? "s" : ""} ·{" "}
                {profiles.filter((p) => p.is_active).length} active
              </p>
            </div>
          </div>
          {mode === null && !isMigrationError && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleExport}
                disabled={profiles.length === 0}
                title="Download all CA profiles as CSV"
                className="inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-white/15 backdrop-blur-sm border border-white/20 text-white text-sm font-body font-semibold rounded-xl hover:bg-white/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="h-4 w-4" /> Export CSV
              </button>
              <button
                onClick={() => setMode("add")}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-[#C2410C] text-sm font-body font-semibold rounded-xl shadow-lg hover:bg-white/95 transition-colors"
              >
                <Plus className="h-4 w-4" /> Add CA Profile
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div
          className={`rounded-xl px-4 py-3 font-body ${isMigrationError ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`}
        >
          <p
            className={`text-sm font-semibold ${isMigrationError ? "text-amber-700" : "text-red-500"}`}
          >
            {error}
          </p>
          {isMigrationError && (
            <div className="mt-2 text-xs text-amber-600 space-y-1">
              <p>Run this SQL in MySQL Workbench, then refresh:</p>
              <pre className="bg-amber-100 rounded px-3 py-2 font-mono overflow-x-auto text-[11px] text-amber-800 whitespace-pre-wrap">{`CREATE TABLE IF NOT EXISTS ca_profiles (
  id INT UNSIGNED NOT NULL AUTO_INCREMENT,
  name VARCHAR(100) NOT NULL,
  registration_no VARCHAR(60) NOT NULL,
  qualification VARCHAR(100) NOT NULL DEFAULT 'Consultant Chartered Accountant',
  email VARCHAR(255) NULL,
  phone VARCHAR(20) NULL,
  experience_yrs INT NOT NULL DEFAULT 0,
  specializations VARCHAR(500) NULL,
  bio TEXT NULL,
  photo_url VARCHAR(500) NULL,
  is_active TINYINT(1) NOT NULL DEFAULT 1,
  created_by INT NOT NULL,
  created_at DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  updated_at DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (id),
  INDEX idx_active (is_active),
  CONSTRAINT fk_ca_profiles_creator FOREIGN KEY (created_by) REFERENCES users(id) ON DELETE RESTRICT
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;`}</pre>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit form */}
      {mode !== null && (
        <div className="bg-white rounded-2xl border-2 border-[#6C63FF]/20 shadow-sm p-6">
          <h3 className="text-sm font-heading font-bold text-[#1A1A2E] mb-5">
            {mode === "add" ? "Add New CA Profile" : `Edit: ${mode.name}`}
          </h3>
          <ProfileForm
            initial={mode === "add" ? null : mode}
            onSave={handleSave}
            onCancel={() => setMode(null)}
          />
        </div>
      )}

      {/* Profile cards grid */}
      {profiles.length === 0 && mode === null ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#E8E6F8] p-12 text-center">
          <Award className="h-10 w-10 text-[#E8E6F8] mx-auto mb-3" />
          <p className="text-sm font-body text-[#9CA3AF]">No CA profiles yet</p>
          <p className="text-xs font-body text-[#9CA3AF] mt-1">
            Add your CA team members to get started
          </p>
          <button
            onClick={() => setMode("add")}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#6C63FF] text-white text-sm font-body rounded-xl mx-auto"
          >
            <Plus className="h-4 w-4" /> Add First CA Profile
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {profiles.map((p) => (
            <ProfileCard
              key={p.id}
              profile={p}
              onEdit={setMode}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
